import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import client, {
  API, formatApiError, fmtTime, fmtDay, dayKey, TYPE_LABEL, fmtHours,
  workedMinutes, hasUnclosedBreak, isoWeekStart, isoWeekId,
} from "../../lib/api";

function StaffManager({ branches, employees, onRefresh }) {
  const [name, setName] = useState("");
  const branch = branches[0] || "";
  const [busy, setBusy] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // { id, name } | null

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      await client.post("/employees", { name: n, branch });
      toast.success(`${n} added`);
      setName("");
      await onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (emp) => {
    if (!window.confirm(`Remove ${emp.name}? Their attendance history will also be deleted.`)) return;
    try {
      await client.delete(`/employees/${emp.id}`);
      toast.success(`${emp.name} removed`);
      await onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const rename = async (emp) => {
    const next = window.prompt(`Rename ${emp.name} to:`, emp.name);
    if (next === null) return; // cancelled
    const trimmed = next.trim();
    if (!trimmed || trimmed === emp.name) return;
    try {
      await client.patch(`/employees/${emp.id}`, { name: trimmed });
      toast.success(`Renamed to ${trimmed}`);
      await onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmResetHistory = async () => {
    if (!resetTarget) return;
    try {
      const { data } = await client.delete(`/employees/${resetTarget.id}/events`);
      toast.success(`Wiped ${data.deleted || 0} events for ${resetTarget.name}`);
      setResetTarget(null);
      await onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="bg-panel border border-line rounded-xl p-4.5 mb-6">
        <div className="caps text-muted-jewel mb-2.5">Add employee</div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Full name"
          data-testid="staff-name-input"
          className="w-full px-3 py-3 bg-panel-soft text-ivory border border-line rounded-lg text-[15px] focus:outline-none mb-2.5"
        />
        <div className="flex gap-2.5">
          <button
            onClick={add}
            disabled={busy || !name.trim() || !branch}
            data-testid="staff-add-btn"
            className="seal-primary w-full rounded-lg px-6 py-3 text-[15px] font-semibold disabled:opacity-40"
          >
            Add
          </button>
        </div>
      </div>

      {branches.map((b) => {
        const staff = employees.filter((e) => e.branch === b);
        return (
          <div key={b} className="mb-5">
            {staff.length === 0 && (
              <div className="text-muted-jewel text-[13px]">None yet.</div>
            )}
            {staff.map((e) => (
              <div
                key={e.id}
                className="flex justify-between items-center py-2.5 border-b border-line gap-3"
                data-testid={`staff-row-${e.id}`}
              >
                <div className="min-w-0">
                  <span className="text-ivory">{e.name}</span>
                </div>
                <span className="inline-flex gap-3.5 items-center shrink-0">
                  <button
                    onClick={() => rename(e)}
                    data-testid={`rename-emp-${e.id}`}
                    className="text-ivory text-[13px] hover:opacity-80 transition-opacity"
                  >
                    Rename
                  </button>
                  <button
                    onClick={() => setResetTarget({ id: e.id, name: e.name })}
                    data-testid={`reset-history-${e.id}`}
                    className="text-muted-jewel text-[13px] hover:text-jewel-red transition-colors"
                    title="Wipe all attendance events for this employee"
                  >
                    Reset history
                  </button>
                  <button
                    onClick={() => remove(e)}
                    data-testid={`remove-emp-${e.id}`}
                    className="text-jewel-red text-[13px] hover:opacity-80 transition-opacity"
                  >
                    Remove
                  </button>
                </span>
              </div>
            ))}
          </div>
        );
      })}

      {resetTarget && (
        <div
          role="dialog"
          data-testid="reset-history-modal"
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setResetTarget(null)}
        >
          <div
            onClick={(ev) => ev.stopPropagation()}
            className="bg-panel border border-jewel-red rounded-2xl p-5 max-w-[420px] w-full shadow-xl"
          >
            <div className="caps text-jewel-red mb-2">Wipe attendance history</div>
            <div className="text-sm text-ivory mb-2">
              This removes <em>every</em> punch, break and manual entry ever recorded for
              <span className="font-semibold"> {resetTarget.name}</span>. Their name stays, so they can punch in cleanly again.
            </div>
            <div className="text-[13px] text-muted-jewel mb-4">
              Use this when a broken punch sequence keeps rejecting new entries and you can't
              find the culprit row in the visible date range. This action is logged in the
              Audit tab.
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={confirmResetHistory}
                data-testid="reset-history-confirm"
                className="bg-jewel-red text-white rounded-full px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                Wipe all events for {resetTarget.name}
              </button>
              <button
                onClick={() => setResetTarget(null)}
                data-testid="reset-history-cancel"
                className="rounded-full border border-line text-muted-jewel px-4 py-2.5 text-sm hover:text-ivory transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default StaffManager;
